// Parsing and error detection for useHttp fetchData results (often JSON.stringify(axios response)).

export interface ParsedFetchEnvelope {
    axiosBody: unknown;
    entity: unknown;
}

export function parseFetchDataEnvelope(raw: unknown): ParsedFetchEnvelope {
    if (raw == null) {
        return { axiosBody: null, entity: null };
    }
    let parsed: unknown = raw;
    if (typeof raw === "string") {
        try {
            parsed = JSON.parse(raw);
        } catch {
            return { axiosBody: null, entity: null };
        }
    }
    if (!parsed || typeof parsed !== "object") {
        return { axiosBody: parsed, entity: parsed };
    }
    const p = parsed as Record<string, unknown>;
    const axiosBody = p.data !== undefined ? p.data : parsed;
    let entity: unknown = axiosBody;
    if (axiosBody && typeof axiosBody === "object") {
        const b = axiosBody as Record<string, unknown>;
        if (b.data !== undefined) {
            entity = b.data;
        }
    }
    return { axiosBody, entity };
}

export function isWorkflowApiLogicalFailure(target: unknown): boolean {
    if (target == null || typeof target !== "object") return false;
    const o = target as Record<string, unknown>;
    if (o.status === false) return true;
    if (o.success === false) return true;
    if ("error" in o && o.error != null && o.error !== "") {
        if (typeof o.error === "boolean" && o.error === false) return false;
        if (typeof o.error === "object" && !Array.isArray(o.error) && Object.keys(o.error as object).length === 0) {
            return false;
        }
        return true;
    }
    return false;
}

export function getWorkflowApiErrorMessage(...candidates: unknown[]): string | undefined {
    for (const target of candidates) {
        if (target == null || typeof target !== "object") continue;
        const o = target as Record<string, unknown>;
        const stringKeys = ["message", "errorMessage", "errorDescription", "description", "msg"] as const;
        for (const key of stringKeys) {
            const v = o[key];
            if (typeof v === "string" && v.trim()) return v.trim();
        }
        const err = o.error;
        if (typeof err === "string" && err.trim()) return err.trim();
        if (err && typeof err === "object") {
            const e = err as Record<string, unknown>;
            const m = e.message ?? e.msg;
            if (typeof m === "string" && m.trim()) return m.trim();
        }
    }
    return undefined;
}

/** Non-empty string status from entity, else from axios body. */
export function pickWorkflowResponseStatus(entity: unknown, axiosBody: unknown): string | undefined {
    for (const target of [entity, axiosBody]) {
        if (!target || typeof target !== "object" || !("status" in target)) continue;
        const st = (target as Record<string, unknown>).status;
        if (typeof st === "string" && st.trim()) return st.trim();
    }
    return undefined;
}

function readNonEmptyId(value: unknown): string | undefined {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
    return undefined;
}

function readRevision(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
        const n = Number(value.trim());
        if (Number.isFinite(n)) return n;
    }
    return undefined;
}

export function readWorkflowUpsertFields(entity: unknown): {
    workflowId?: string;
    revision?: number;
    status?: string;
} {
    if (entity == null || typeof entity !== "object") return {};
    const o = entity as Record<string, unknown>;
    const status = o.status;
    return {
        workflowId: readNonEmptyId(o.workflowId),
        revision: readRevision(o.revision),
        status: typeof status === "string" && status.trim() ? status.trim() : undefined,
    };
}

export function extractWorkflowPayload(env: ParsedFetchEnvelope): Record<string, unknown> | null {
    if (env.entity && typeof env.entity === "object" && "graph" in env.entity) {
        return env.entity as Record<string, unknown>;
    }
    if (env.axiosBody && typeof env.axiosBody === "object" && "graph" in env.axiosBody) {
        return env.axiosBody as Record<string, unknown>;
    }
    return null;
}
